import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { datetimeLocalToUTC } from '../utils/dateHelpers';
import DateTimePickerInput from './DateTimePickerInput';
import { stripHtml as sanitizeStripHtml } from '../utils/sanitize';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface DuplicateChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  change: any;
  onSuccess?: (duplicatedChange: any) => void;
}

// Option types for comboboxes
interface SelectOption {
  id: string;
  label: string;
  sublabel?: string;
}

const DuplicateChangeModal: React.FC<DuplicateChangeModalProps> = ({
  isOpen,
  onClose,
  change,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { preferences } = useDateFormatter();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Combobox query states
  const [changeTypeQuery, setChangeTypeQuery] = useState('');
  const [riskAssessmentQuery, setRiskAssessmentQuery] = useState('');
  const [impactQuery, setImpactQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [approvalGroupQuery, setApprovalGroupQuery] = useState('');

  const [formData, setFormData] = useState({
    changeType: 'normal',
    riskAssessment: 'moderate',
    impact: 'medium_2',
    priority: 'moderate_3',
    customerId: '',
    engagementId: '',
    callerId: '',
    contactId: '',
    resourceId: '',
    resourceIdValue: '',
    otherResourceNames: '',
    assignmentGroupId: '',
    assignedTo: '',
    cabRequired: true,
    approvalGroupId: '',
    plannedStart: '',
    plannedEnd: '',
    shortDescription: '',
    description: '',
    justification: '',
    riskImpactAnalysis: '',
    implementationPlan: '',
    backoutPlan: '',
    testPlan: ''
  });

  const [isOtherResource, setIsOtherResource] = useState(false);

  // Static options
  const changeTypeOptions: SelectOption[] = [
    { id: 'normal', label: 'Normal' },
    { id: 'standard', label: 'Standard' },
    { id: 'emergency', label: 'Emergency' },
    { id: 'infrastructure', label: 'Infrastructure' }
  ];

  const riskAssessmentOptions: SelectOption[] = [
    { id: 'high', label: 'High' },
    { id: 'moderate', label: 'Moderate' },
    { id: 'low', label: 'Low' }
  ];

  const impactOptions: SelectOption[] = [
    { id: 'high_1', label: 'High' },
    { id: 'medium_2', label: 'Medium' },
    { id: 'low_3', label: 'Low' }
  ];

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements'],
    queryFn: async () => {
      const response = await api.get('/engagements');
      return response.data;
    }
  });

  // Fetch assignment groups
  const { data: groupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    }
  });

  // Fetch approval groups
  const { data: approvalGroupsData } = useQuery({
    queryKey: ['approval-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/approval-groups');
      return response.data;
    }
  });

  // Fetch users based on assignment group
  const { data: usersData } = useQuery({
    queryKey: ['group-users', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      return fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroupId
  });


  // Fetch contacts for Contact field (filtered by engagement)
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) {
        return { contacts: [] };
      }
      const response = await api.get(`/contacts/dropdown?engagementId=${formData.engagementId}`);
      return response.data;
    },
    enabled: !!formData.engagementId,
    staleTime: 0,
    refetchOnMount: true
  });

  // Fetch primary contact for auto-selection
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch resources based on engagement
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) {
        return [];
      }
      const response = await api.get(`/resources?engagementId=${formData.engagementId}`);
      return Array.isArray(response.data) ? response.data : (response.data.resources || []);
    },
    enabled: !!formData.engagementId,
    staleTime: 0,
    refetchOnMount: true
  });

  // Update Resource ID value when resource changes
  useEffect(() => {
    if (formData.resourceId && resourcesData && !isOtherResource) {
      const resourcesArray = Array.isArray(resourcesData) ? resourcesData : [];
      const selectedResource = resourcesArray.find((r: any) => r.id === formData.resourceId);
      if (selectedResource) {
        setFormData(prev => ({ ...prev, resourceIdValue: selectedResource.resourceId || '' }));
      }
    } else if (!formData.resourceId && !isOtherResource) {
      setFormData(prev => ({ ...prev, resourceIdValue: '' }));
    }
  }, [formData.resourceId, resourcesData, isOtherResource]);

  // Auto-select primary contact when customer/engagement changes
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId]);

  // Priority calculation based on Impact and Risk Assessment
  const calculatePriority = (impact: string, risk: string): string => {
    const normalizeValue = (value: string): string => {
      if (!value) return 'low';
      if (value.startsWith('high')) return 'high';
      if (value.startsWith('medium')) return 'medium';
      if (value.startsWith('low')) return 'low';
      return value;
    };

    const normalizedImpact = normalizeValue(impact);
    const normalizedRisk = normalizeValue(risk);

    const matrix: Record<string, Record<string, string>> = {
      'high': { 'high': 'critical_1', 'moderate': 'high_2', 'low': 'moderate_3' },
      'medium': { 'high': 'high_2', 'moderate': 'moderate_3', 'low': 'low_4' },
      'low': { 'high': 'moderate_3', 'moderate': 'low_4', 'low': 'planning_5' }
    };

    return matrix[normalizedImpact]?.[normalizedRisk] || 'moderate_3';
  };

  // Auto-calculate priority when impact or risk assessment changes
  useEffect(() => {
    if (formData.impact && formData.riskAssessment) {
      const calculatedPriority = calculatePriority(formData.impact, formData.riskAssessment);
      setFormData(prev => ({ ...prev, priority: calculatedPriority }));
    }
  }, [formData.impact, formData.riskAssessment]);

  // Auto-set CAB Required based on Change Type
  useEffect(() => {
    const cabRequired = formData.changeType === 'normal' || formData.changeType === 'emergency';
    setFormData(prev => ({
      ...prev,
      cabRequired,
      approvalGroupId: cabRequired ? prev.approvalGroupId : ''
    }));
  }, [formData.changeType]);

  // Pre-fill form when change data changes
  useEffect(() => {
    if (change && isOpen) {
      const normalizeValue = (value: string): string => {
        if (!value) return 'moderate';
        if (value.startsWith('high')) return 'high';
        if (value.startsWith('medium')) return 'medium';
        if (value.startsWith('low')) return 'low';
        return value;
      };

      const impact = change.impact || 'medium_2';
      const riskAssessment = normalizeValue(change.risk) || 'moderate';
      const calculatedPriority = calculatePriority(impact, riskAssessment);

      const changeType = change.changeType || 'normal';
      const cabRequired = changeType === 'normal' || changeType === 'emergency';

      // Check if source has otherResourceNames
      const hasOtherResourceNames = !!(change.otherResourceNames && change.otherResourceNames.trim());
      setIsOtherResource(hasOtherResourceNames);

      setFormData({
        changeType: changeType,
        riskAssessment: riskAssessment,
        impact: impact,
        priority: calculatedPriority,
        customerId: change.customerId || '',
        engagementId: change.engagementId || '',
        callerId: change.callerId || '',
        contactId: change.contactId || '',
        resourceId: hasOtherResourceNames ? '' : (change.resourceId || ''),
        resourceIdValue: hasOtherResourceNames ? '' : (change.resourceIdValue || ''),
        otherResourceNames: change.otherResourceNames || '',
        assignmentGroupId: '',
        assignedTo: '',
        cabRequired: cabRequired,
        approvalGroupId: '',
        plannedStart: '',
        plannedEnd: '',
        shortDescription: change.shortDescription ? `Copy of ${change.shortDescription}` : 'Copy of Change',
        description: change.description || '',
        justification: change.justification || '',
        riskImpactAnalysis: change.riskImpactAnalysis || '',
        implementationPlan: change.implementationPlan || '',
        backoutPlan: change.backoutPlan || '',
        testPlan: change.testPlan || ''
      });
      setErrorMessage(null);
      setValidationErrors({});
    }
  }, [change, isOpen]);

  const duplicateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/changes', data);
      return response.data;
    },
    onSuccess: async (data) => {
      const caseId = data?.id || data?._id;

      if (!caseId) {
        console.error('Failed to get change ID from response:', data);
        setErrorMessage('Change created but failed to get ID');
        onClose();
        return;
      }

      try {
        await api.post(`/cases/${caseId}/related-records`, {
          relationships: [{
            targetId: change.id,
            relationshipType: 'RELATED_TO'
          }]
        });
      } catch (error) {
        console.error('Failed to create related records:', error);
      }

      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['changes'] });
      queryClient.invalidateQueries({ queryKey: ['related-records', change.id] });
      queryClient.invalidateQueries({ queryKey: ['related-records', caseId] });
      if (onSuccess) {
        onSuccess(data);
      }
      onClose();
      navigate(`/changes/${caseId}`);
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || 'Failed to duplicate change';
      setErrorMessage(errorMsg);
    }
  });

  // Helper function to strip HTML for validation
  // SECURITY: Uses DOMPurify-based stripHtml for safe HTML removal
  const stripHtml = (html: string): string => {
    return sanitizeStripHtml(html) || '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const errors: Record<string, string> = {};

    if (!formData.impact) {
      errors.impact = 'Impact is required';
    }
    if (!formData.customerId) {
      errors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      errors.engagementId = 'Engagement is required';
    }
    if (!formData.contactId) {
      errors.contactId = 'Contact is required';
    }
    if (!isOtherResource && !formData.resourceId) {
      errors.resourceId = 'Resource is required';
    }
    if (isOtherResource && !formData.otherResourceNames.trim()) {
      errors.otherResourceNames = 'Resource Names is required';
    }
    if (!formData.assignmentGroupId) {
      errors.assignmentGroupId = 'Assignment Group is required';
    }
    if (formData.cabRequired && !formData.approvalGroupId) {
      errors.approvalGroupId = 'Approval Group is required when CAB is required';
    }
    if (!formData.plannedStart) {
      errors.plannedStart = 'Planned Start is required';
    }
    if (!formData.plannedEnd) {
      errors.plannedEnd = 'Planned End is required';
    }
    if (!formData.shortDescription.trim()) {
      errors.shortDescription = 'Short Description is required';
    }
    if (!stripHtml(formData.justification).trim()) {
      errors.justification = 'Justification is required';
    }
    if (!stripHtml(formData.riskImpactAnalysis).trim()) {
      errors.riskImpactAnalysis = 'Risk & Impact Analysis is required';
    }
    if (!stripHtml(formData.implementationPlan).trim()) {
      errors.implementationPlan = 'Implementation Plan is required';
    }
    if (!stripHtml(formData.backoutPlan).trim()) {
      errors.backoutPlan = 'Backout Plan is required';
    }
    if (!stripHtml(formData.testPlan).trim()) {
      errors.testPlan = 'Test Plan is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setValidationErrors({});
    setErrorMessage(null);

    const payload: any = {
      shortDescription: formData.shortDescription,
      description: formData.description,
      changeType: formData.changeType,
      risk: formData.riskAssessment,
      impact: formData.impact,
      cabRequired: formData.cabRequired,
      priority: formData.priority,
      category: 'change',
      urgency: formData.impact,
      customerId: formData.customerId,
      engagementId: formData.engagementId,
      callerId: formData.callerId,
      contactId: formData.contactId,
      assignedToId: formData.assignedTo,
      assignmentGroupId: formData.assignmentGroupId || undefined,
      approvalGroupId: formData.cabRequired ? formData.approvalGroupId : undefined,
      justification: formData.justification,
      riskImpactAnalysis: formData.riskImpactAnalysis,
      implementationPlan: formData.implementationPlan,
      backoutPlan: formData.backoutPlan,
      testPlan: formData.testPlan,
      plannedStart: datetimeLocalToUTC(formData.plannedStart, preferences.timezone),
      plannedEnd: datetimeLocalToUTC(formData.plannedEnd, preferences.timezone),
      // Source tracking for activity logging
      duplicatedFromChangeId: change.id,
      duplicatedFromChangeNumber: change.number
    };

    // Handle resource selection: either otherResourceNames or resourceId
    if (isOtherResource) {
      payload.otherResourceNames = formData.otherResourceNames;
    } else {
      payload.resourceId = formData.resourceId;
      payload.resourceIdValue = formData.resourceIdValue;
    }

    duplicateMutation.mutate(payload);
  };

  const customers = customersData?.customers || [];
  const allEngagements = engagementsData?.engagements || [];

  const engagements = formData.customerId
    ? allEngagements.filter((e: any) => e.Customer?.id === formData.customerId)
    : allEngagements;

  const groups = groupsData || [];
  const approvalGroups = approvalGroupsData || [];
  const groupUsers = usersData || [];

  // Helper function to get priority label
  const getPriorityLabel = (priority: string): string => {
    const labels: Record<string, string> = {
      'critical_1': 'Critical',
      'high_2': 'High',
      'moderate_3': 'Moderate',
      'low_4': 'Low',
      'planning_5': 'Planning'
    };
    return labels[priority] || 'Moderate';
  };

  // Helper function to filter options
  const filterOptions = (options: SelectOption[], query: string): SelectOption[] => {
    if (!query) return options;
    return options.filter(option =>
      option.label.toLowerCase().includes(query.toLowerCase()) ||
      (option.sublabel && option.sublabel.toLowerCase().includes(query.toLowerCase()))
    );
  };

  // Transform data to SelectOption format
  const customerOptions: SelectOption[] = customers.map((c: any) => ({
    id: c.id,
    label: c.accountName
  }));

  const engagementOptions: SelectOption[] = engagements.map((e: any) => ({
    id: e.id,
    label: e.name
  }));

  const contactOptions: SelectOption[] = (contactsData?.contacts || []).map((c: any) => ({
    id: c.id,
    label: c.name
  }));

  const resourceOptions: SelectOption[] = (Array.isArray(resourcesData) ? resourcesData : []).map((r: any) => ({
    id: r.id,
    label: r.name
  }));

  const assignmentGroupOptions: SelectOption[] = groups.map((g: any) => ({
    id: g.id,
    label: g.name
  }));

  const assignedToOptions: SelectOption[] = groupUsers.map((u: any) => ({
    id: u.id,
    label: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email,
    sublabel: u.email
  }));

  const approvalGroupOptions: SelectOption[] = approvalGroups.map((g: any) => ({
    id: g.id,
    label: g.name
  }));

  // Generic Combobox component
  const renderCombobox = (
    value: string,
    onChange: (value: string) => void,
    options: SelectOption[],
    query: string,
    setQuery: (query: string) => void,
    placeholder: string,
    disabled: boolean = false,
    hasError: boolean = false
  ) => {
    const filteredOptions = filterOptions(options, query);
    const selectedOption = options.find(o => o.id === value);

    return (
      <Combobox value={value} onChange={onChange} disabled={disabled}>
        <div className="relative">
          <div className="relative">
            <Combobox.Input
              className={`block w-full rounded-md border ${hasError ? 'border-red-500' : 'border-gray-300'} ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
              displayValue={() => selectedOption ? (selectedOption.sublabel ? `${selectedOption.label} - ${selectedOption.sublabel}` : selectedOption.label) : ''}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={placeholder}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredOptions.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  Nothing found.
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={option.id}
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                          {option.label}
                          {option.sublabel && (
                            <span className={`ml-2 ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                              {option.sublabel}
                            </span>
                          )}
                        </span>
                        {selected && (
                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]">
                {/* Static Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Duplicate Change {change?.number}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Scrollable Form Body */}
                <div className="overflow-y-auto flex-1 px-6 py-4">
                  <form id="duplicate-change-form" onSubmit={handleSubmit} className="space-y-4">
                    {/* Error Message */}
                    {errorMessage && (
                      <div className="rounded-md bg-red-50 p-4">
                        <div className="text-sm text-red-700">{errorMessage}</div>
                      </div>
                    )}

                    {/* Row 1: Change Type and State */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Change Type */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Change Type <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {renderCombobox(
                            formData.changeType,
                            (value) => setFormData({ ...formData, changeType: value }),
                            changeTypeOptions,
                            changeTypeQuery,
                            setChangeTypeQuery,
                            'Select change type'
                          )}
                        </div>
                      </div>

                      {/* State (Disabled - new duplicated change always starts as New) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          State
                        </label>
                        <div className="flex-1">
                          <input
                            type="text"
                            value="New"
                            disabled
                            className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Risk Assessment and Impact */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Risk Assessment */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Risk Assessment <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {renderCombobox(
                            formData.riskAssessment,
                            (value) => setFormData({ ...formData, riskAssessment: value }),
                            riskAssessmentOptions,
                            riskAssessmentQuery,
                            setRiskAssessmentQuery,
                            'Select risk assessment'
                          )}
                        </div>
                      </div>

                      {/* Impact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Impact <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {renderCombobox(
                            formData.impact,
                            (value) => setFormData({ ...formData, impact: value }),
                            impactOptions,
                            impactQuery,
                            setImpactQuery,
                            'Select impact',
                            false,
                            !!validationErrors.impact
                          )}
                          {validationErrors.impact && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.impact}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Priority (alone - auto-calculated) */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Priority (Auto-calculated) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Priority
                        </label>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={formData.priority ? getPriorityLabel(formData.priority) : ''}
                            disabled
                            className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            placeholder="Auto-calculated from Impact & Risk"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Row 4: Customer and Engagement */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Customer */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Customer <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {renderCombobox(
                            formData.customerId,
                            (value) => {
                              setFormData({ ...formData, customerId: value, engagementId: '', resourceId: '', resourceIdValue: '', otherResourceNames: '', callerId: '' });
                              setIsOtherResource(false);
                            },
                            customerOptions,
                            customerQuery,
                            setCustomerQuery,
                            'Select customer',
                            false,
                            !!validationErrors.customerId
                          )}
                          {validationErrors.customerId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.customerId}</p>
                          )}
                        </div>
                      </div>

                      {/* Engagement */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Engagement <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {renderCombobox(
                            formData.engagementId,
                            (value) => {
                              setFormData({ ...formData, engagementId: value, resourceId: '', resourceIdValue: '', otherResourceNames: '', contactId: '' });
                              setIsOtherResource(false);
                            },
                            engagementOptions,
                            engagementQuery,
                            setEngagementQuery,
                            formData.customerId ? 'Select engagement' : 'Select customer first',
                            !formData.customerId,
                            !!validationErrors.engagementId
                          )}
                          {validationErrors.engagementId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.engagementId}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 5: Contact (alone - matching ChangeCreate.tsx layout) */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Contact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Contact <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {renderCombobox(
                            formData.contactId,
                            (value) => setFormData({ ...formData, contactId: value }),
                            contactOptions,
                            contactQuery,
                            setContactQuery,
                            formData.customerId ? 'Select contact' : 'Select customer first',
                            !formData.engagementId,
                            !!validationErrors.contactId
                          )}
                          {validationErrors.contactId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.contactId}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 6: Resource and Resource ID */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Resource */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Resource <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={isOtherResource ? '__OTHER__' : formData.resourceId}
                            onChange={(value: string) => {
                              if (value === '__OTHER__') {
                                setIsOtherResource(true);
                                setFormData({ ...formData, resourceId: '', resourceIdValue: '' });
                              } else {
                                setIsOtherResource(false);
                                setFormData({ ...formData, resourceId: value, otherResourceNames: '' });
                              }
                            }}
                            disabled={!formData.engagementId}
                          >
                            <div className="relative">
                              <div className="relative">
                                <Combobox.Input
                                  className={`block w-full rounded-md border ${validationErrors.resourceId ? 'border-red-500' : 'border-gray-300'} ${!formData.engagementId ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                  displayValue={() => {
                                    if (isOtherResource) return 'Others (specify custom resources)';
                                    const selectedOption = resourceOptions.find(o => o.id === formData.resourceId);
                                    return selectedOption ? (selectedOption.sublabel ? `${selectedOption.label} - ${selectedOption.sublabel}` : selectedOption.label) : '';
                                  }}
                                  onChange={(event) => setResourceQuery(event.target.value)}
                                  placeholder={formData.engagementId ? 'Select resource' : 'Select engagement first'}
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setResourceQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filterOptions(resourceOptions, resourceQuery).length === 0 && resourceQuery !== '' ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      Nothing found.
                                    </div>
                                  ) : (
                                    <>
                                      {filterOptions(resourceOptions, resourceQuery).map((option) => (
                                        <Combobox.Option
                                          key={option.id}
                                          className={({ active }) =>
                                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                              active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                            }`
                                          }
                                          value={option.id}
                                        >
                                          {({ selected, active }) => (
                                            <>
                                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                {option.label}
                                                {option.sublabel && (
                                                  <span className={`ml-2 ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                                                    {option.sublabel}
                                                  </span>
                                                )}
                                              </span>
                                              {selected && (
                                                <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </Combobox.Option>
                                      ))}
                                      <Combobox.Option
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 border-t border-gray-200 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                        value="__OTHER__"
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              Others (specify custom resources)
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    </>
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.resourceId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.resourceId}</p>
                          )}
                        </div>
                      </div>

                      {/* Resource ID or Resource Names */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          {isOtherResource ? 'Resource Names' : 'Resource ID'} {isOtherResource && <span className="text-red-500">*</span>}
                        </label>
                        <div className="flex-1">
                          {isOtherResource ? (
                            <>
                              <input
                                type="text"
                                value={formData.otherResourceNames}
                                onChange={(e) => setFormData({ ...formData, otherResourceNames: e.target.value })}
                                className={`block w-full rounded-md border ${validationErrors.otherResourceNames ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                placeholder="Enter custom resource names"
                              />
                              {validationErrors.otherResourceNames && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.otherResourceNames}</p>
                              )}
                            </>
                          ) : (
                            <input
                              type="text"
                              value={formData.resourceIdValue}
                              disabled
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                              placeholder="Auto-filled from Resource"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 7: Planned Start and Planned End */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Planned Start */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Planned Start <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <DateTimePickerInput
                            value={formData.plannedStart}
                            onChange={(value) => setFormData({ ...formData, plannedStart: value })}
                            error={!!validationErrors.plannedStart}
                            required
                          />
                          {validationErrors.plannedStart && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.plannedStart}</p>
                          )}
                        </div>
                      </div>

                      {/* Planned End */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Planned End <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <DateTimePickerInput
                            value={formData.plannedEnd}
                            onChange={(value) => setFormData({ ...formData, plannedEnd: value })}
                            min={formData.plannedStart || undefined}
                            error={!!validationErrors.plannedEnd}
                            required
                          />
                          {validationErrors.plannedEnd && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.plannedEnd}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 8: Assignment Group and Assigned To */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Assignment Group */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {renderCombobox(
                            formData.assignmentGroupId,
                            (value) => {
                              setFormData({ ...formData, assignmentGroupId: value, assignedTo: '' });
                              if (validationErrors.assignmentGroupId) {
                                setValidationErrors(prev => ({ ...prev, assignmentGroupId: '' }));
                              }
                            },
                            assignmentGroupOptions,
                            assignmentGroupQuery,
                            setAssignmentGroupQuery,
                            'Select group',
                            false,
                            !!validationErrors.assignmentGroupId
                          )}
                          {validationErrors.assignmentGroupId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignmentGroupId}</p>
                          )}
                        </div>
                      </div>

                      {/* Assigned To */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assigned To
                        </label>
                        <div className="flex-1">
                          {renderCombobox(
                            formData.assignedTo,
                            (value) => setFormData({ ...formData, assignedTo: value }),
                            assignedToOptions,
                            assignedToQuery,
                            setAssignedToQuery,
                            formData.assignmentGroupId ? 'Select assignee' : 'Select assignment group first',
                            !formData.assignmentGroupId,
                            !!validationErrors.assignedTo
                          )}
                          {validationErrors.assignedTo && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignedTo}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 9: CAB Required and Approval Group */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* CAB Required */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          CAB Required
                        </label>
                        <div className="flex-1 pt-2">
                          <input
                            type="checkbox"
                            checked={formData.cabRequired}
                            disabled
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-not-allowed"
                          />
                          <span className="ml-2 text-sm text-gray-500">
                            (Auto-set for Normal and Emergency changes)
                          </span>
                        </div>
                      </div>

                      {/* Approval Group - Only shown when CAB Required */}
                      {formData.cabRequired && (
                        <div className="flex items-start gap-3">
                          <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                            Approval Group <span className="text-red-500">*</span>
                          </label>
                          <div className="flex-1">
                            {renderCombobox(
                              formData.approvalGroupId,
                              (value) => setFormData({ ...formData, approvalGroupId: value }),
                              approvalGroupOptions,
                              approvalGroupQuery,
                              setApprovalGroupQuery,
                              'Select approval group',
                              false,
                              !!validationErrors.approvalGroupId
                            )}
                            {validationErrors.approvalGroupId && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.approvalGroupId}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Row 10: Short Description */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Short Description <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={formData.shortDescription}
                          onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                          className={`block w-full rounded-md border ${validationErrors.shortDescription ? 'border-red-500' : 'border-gray-300'} shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                        />
                        {validationErrors.shortDescription && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.shortDescription}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 11: Description */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Description
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={formData.description}
                          onChange={(value) => setFormData({ ...formData, description: value })}
                          placeholder="Enter description..."
                          minHeight="100px"
                        />
                      </div>
                    </div>

                    {/* Row 12: Justification */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Justification <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={formData.justification}
                          onChange={(value) => setFormData({ ...formData, justification: value })}
                          placeholder="Enter justification..."
                          minHeight="100px"
                        />
                        {validationErrors.justification && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.justification}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 13: Risk & Impact Analysis */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Risk & Impact Analysis <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={formData.riskImpactAnalysis}
                          onChange={(value) => setFormData({ ...formData, riskImpactAnalysis: value })}
                          placeholder="Enter risk and impact analysis..."
                          minHeight="100px"
                        />
                        {validationErrors.riskImpactAnalysis && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.riskImpactAnalysis}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 14: Implementation Plan */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Implementation Plan <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={formData.implementationPlan}
                          onChange={(value) => setFormData({ ...formData, implementationPlan: value })}
                          placeholder="Enter implementation plan..."
                          minHeight="100px"
                        />
                        {validationErrors.implementationPlan && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.implementationPlan}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 15: Backout Plan */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Backout Plan <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={formData.backoutPlan}
                          onChange={(value) => setFormData({ ...formData, backoutPlan: value })}
                          placeholder="Enter backout plan..."
                          minHeight="100px"
                        />
                        {validationErrors.backoutPlan && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.backoutPlan}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 16: Test Plan */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Test Plan <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={formData.testPlan}
                          onChange={(value) => setFormData({ ...formData, testPlan: value })}
                          placeholder="Enter test plan..."
                          minHeight="100px"
                        />
                        {validationErrors.testPlan && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.testPlan}</p>
                        )}
                      </div>
                    </div>
                  </form>
                </div>

                {/* Static Footer */}
                <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="duplicate-change-form"
                    disabled={duplicateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                  >
                    {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate Change'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DuplicateChangeModal;
